import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import axios from 'axios';
import { FaExclamationTriangle, FaMapMarkerAlt, FaCrosshairs } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { getTerminalName } from '../utils/terminalNames';
import { REGIONAL } from '../config/regional';

const FitToMarkers = ({ points }) => {
  const map = useMap();
  useEffect(() => {
    if (!points?.length) return;
    if (points.length === 1) {
      map.setView(points[0], 13);
      return;
    }
    const bounds = L.latLngBounds(points);
    map.fitBounds(bounds, { padding: [40, 40] });
  }, [points, map]);
  return null;
};

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const MapaPage = ({ token }) => {
  const [terminales, setTerminales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const markerPoints = terminales.map((t) => [Number(t.latitud), Number(t.longitud)]);

  useEffect(() => {
    const fetchTerminales = async () => {
      try {
        const apiUrl = process.env.REACT_APP_API_URL;
        const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
        const [terminalesRes, lecturasRes] = await Promise.all([
          axios.get(`${apiUrl}/terminales/getTerminales`, { headers }),
          axios.get(`${apiUrl}/lecturas/terminales?days=1`, { headers }),
        ]);

        if (terminalesRes.data.cod === 200 && Array.isArray(terminalesRes.data.data)) {
          const latestByGuid = {};
          if (lecturasRes.data.cod === 200 && Array.isArray(lecturasRes.data.data)) {
            lecturasRes.data.data.forEach((row) => {
              latestByGuid[row.guid] = row;
            });
          }

          const withLatest = terminalesRes.data.data
            .filter((t) => t.latitud && t.longitud)
            .map((t) => ({ ...t, latest: latestByGuid[t.guid] || null }));

          setTerminales(withLatest);
        }
      } catch (error) {
        if (error.response && [400, 401, 403].includes(error.response.status)) {
          navigate('/login');
        } else {
          setError('Error al cargar ubicaciones');
        }
      } finally {
        setLoading(false);
      }
    };
    fetchTerminales();
  }, [token, navigate]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-4">
        <h1 className="text-2xl font-bold text-gray-900">Mapa</h1>
        <p className="text-sm text-gray-500 mt-1">{terminales.length} terminal{terminales.length !== 1 ? 'es' : ''} con ubicación</p>
      </div>

      {error && (
        <div className="flex items-center gap-3 px-4 py-3 bg-amber-50 border border-amber-200 text-amber-700 text-sm rounded-lg mb-4">
          <FaExclamationTriangle className="w-4 h-4 shrink-0" /> {error}
        </div>
      )}

      <div className="rounded-xl overflow-hidden border border-gray-200 shadow-sm" style={{ height: 'calc(100vh - 12rem)' }}>
        <MapContainer center={REGIONAL.mapCenter} zoom={10} style={{ height: '100%', width: '100%' }}>
          <TileLayer
            attribution='&copy; <a href="http://osm.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <FitToMarkers points={markerPoints} />
          {terminales.map((terminal) => (
            <Marker key={terminal.guid} position={[Number(terminal.latitud), Number(terminal.longitud)]}>
              <Popup>
                <div className="min-w-[180px]">
                  <h3 className="font-bold text-gray-900 mb-1">{getTerminalName(terminal)}</h3>
                  <p className="text-[11px] text-gray-500 font-mono mb-1">GUID: {terminal.guid}</p>
                  <div className="space-y-0.5 text-xs text-gray-600">
                    <p><FaMapMarkerAlt className="inline w-3 h-3 mr-1" />{terminal.ubicacion || 'Sin ubicación'}</p>
                    <p>Estado: {terminal.estado ? 'Activo' : 'Inactivo'}</p>
                    {terminal.descripcion && <p className="mt-1 text-gray-500">{terminal.descripcion}</p>}
                  </div>
                  {terminal.latest ? (
                    <div className="mt-2 pt-2 border-t border-gray-200 text-xs text-gray-700 space-y-1">
                      <p className="font-semibold text-gray-800">Ultima lectura</p>
                      <p className="text-[11px] text-gray-500">{terminal.latest.fecha} {String(terminal.latest.hora || '').slice(0, 8)}</p>
                      <div className="grid grid-cols-2 gap-1.5 mt-1">
                        <div className="bg-gray-50 rounded px-2 py-1">
                          <p className="text-[10px] text-gray-500">Temp aire</p>
                          <p className="font-semibold">{Number(terminal.latest.temperature_air || 0).toFixed(1)} C</p>
                        </div>
                        <div className="bg-gray-50 rounded px-2 py-1">
                          <p className="text-[10px] text-gray-500">Hum aire</p>
                          <p className="font-semibold">{Number(terminal.latest.humidity_air || 0).toFixed(1)} %</p>
                        </div>
                        <div className="bg-gray-50 rounded px-2 py-1">
                          <p className="text-[10px] text-gray-500">Temp 30cm</p>
                          <p className="font-semibold">{Number(terminal.latest.temperature_soil_30cm || 0).toFixed(1)} C</p>
                        </div>
                        <div className="bg-gray-50 rounded px-2 py-1">
                          <p className="text-[10px] text-gray-500">Hum 30cm</p>
                          <p className="font-semibold">{Number(terminal.latest.humidity_soil_30cm || 0).toFixed(1)} %</p>
                        </div>
                        <div className="bg-gray-50 rounded px-2 py-1">
                          <p className="text-[10px] text-gray-500">Temp 60cm</p>
                          <p className="font-semibold">{Number(terminal.latest.temperature_soil_60cm || 0).toFixed(1)} C</p>
                        </div>
                        <div className="bg-gray-50 rounded px-2 py-1">
                          <p className="text-[10px] text-gray-500">Hum 60cm</p>
                          <p className="font-semibold">{Number(terminal.latest.humidity_soil_60cm || 0).toFixed(1)} %</p>
                        </div>
                        <div className="bg-gray-50 rounded px-2 py-1">
                          <p className="text-[10px] text-gray-500">Cond 30cm</p>
                          <p className="font-semibold">{Number(terminal.latest.conductivity_soil_30cm || 0).toFixed(1)} uS/cm</p>
                        </div>
                        <div className="bg-gray-50 rounded px-2 py-1">
                          <p className="text-[10px] text-gray-500">Cond 60cm</p>
                          <p className="font-semibold">{Number(terminal.latest.conductivity_soil_60cm || 0).toFixed(1)} uS/cm</p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <p className="mt-2 pt-2 border-t border-gray-200 text-xs text-gray-500">Sin lecturas recientes</p>
                  )}
                  <button onClick={() => navigate(`/graficos/${terminal.guid}`)}
                    className="mt-2 w-full text-center text-xs bg-emerald-50 hover:bg-emerald-100 text-emerald-700 font-medium py-1.5 rounded-lg transition-colors">
                    Ver Gráficos
                  </button>
                </div>
              </Popup>
            </Marker>
          ))}
          {terminales.length === 0 && !loading && (
            <div className="absolute top-4 left-1/2 -translate-x-1/2 z-[1000] bg-white px-4 py-2 rounded-lg shadow-lg border border-gray-200 text-sm text-gray-500 flex items-center gap-2">
              <FaCrosshairs className="w-4 h-4" /> No hay terminales con ubicación para mostrar
            </div>
          )}
        </MapContainer>
      </div>
    </div>
  );
};

export default MapaPage;
